import * as $ from 'jquery';

import { ILoggerService, ISessionStorageService } from './';
import { authorizationDataKey, workspaceKey, signalRConnectionStateChange } from '../../common';
import { IRootScope } from '../../IRootScope';
import { ISignalRConnectionStateChange } from '../../models';

export interface ISignalrService {
    connection: SignalR.Hub.Connection;
    isConnected(): boolean;
    connect(code?: () => void): angular.IPromise<void>;
    disconnect(): void;
    setBaseUrl(url: string): void;
    start(): angular.IPromise<void>;
}

export class SignalrService implements ISignalrService {
    static serviceId = 'signalrService';

    connection: SignalR.Hub.Connection;

    private _isConnected: boolean = false;

    static $inject = [
        '$q',
        '$rootScope',
        'loggerService',
        'sessionStorageService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$rootScope: IRootScope,
        private loggerService: ILoggerService,
        private sessionStorageService: ISessionStorageService
    ) {
    }

    private initConnection(baseUrl: string): void {
        if (!this.connection?.url) {
            this.connection = $.hubConnection(baseUrl);
            if (this.setHeaders()) {
                this.connection.error((error: any): void => {
                    const message = `SignalR: Error - ${error}`;
                    this.loggerService.logError(message, false);
                });
                if ((this.connection.qs as any).user) {
                    this.connection.stateChanged((change: SignalR.StateChanged): void => {
                        if (change.newState === $.signalR.connectionState.disconnected || change.newState === $.signalR.connectionState.reconnecting) {
                            this._isConnected = false;
                        } else if (change.newState === $.signalR.connectionState.connected) {
                            this._isConnected = true;
                        }
                        const data: ISignalRConnectionStateChange = { isConnected: this._isConnected, newState: change.newState, oldState: change.oldState };
                        this._$rootScope.$broadcast(signalRConnectionStateChange, data);
                    });
                }
            }
        }
    }

    private setHeaders(): boolean {
        const workspace = this.sessionStorageService.getFrom(workspaceKey);
        this.connection.qs = { 'workspace': workspace };
        const authData = JSON.parse(
            this.sessionStorageService.getFrom(authorizationDataKey)
        );
        if (authData) {
            (this.connection.qs as any).user = authData.userName;
        }
        return (this.connection.qs as any).user ? true : false;
    }

    connect(code: () => void = undefined): angular.IPromise<void> {
        const result = this._$q.defer<void>();
        if (this.setHeaders()) {
            if (this.connection?.url && this.connection.state !== $.signalR.connectionState.connected || this.connection.state !== $.signalR.connectionState.connecting) {
                this.connection
                    .start()
                    .done(() => {
                        if (code) {
                            code();
                        }
                        result.resolve();
                    })
                    .fail(error => {
                        console.error(error);
                        result.reject(error);
                    });
            } else {
                code();
                result.resolve();
            }
        }
        return result.promise;
    }

    disconnect(): void {
        // To be imporved: if the connection is closed before being open, outputs a console error in signalr
        if (this.connection && this.connection.state !== $.signalR.connectionState.disconnected) {
            this.connection.stop();
        }
    }

    isConnected(): boolean {
        return this._isConnected;
    }

    setBaseUrl(url: string): void {
        if (url.startsWith('/')) {
            url = `${location.protocol}//${location.host}${url}`;
            if (url.endsWith('/')) {
                url = url.substr(0, url.length - 1);
            }
        }
        this.initConnection(url);
    }

    start(): angular.IPromise<void> {
        return this.connection.state === $.signalR.connectionState.disconnected
            ? this._$q.when(this.connection.start())
            : this._$q.when();
    }
}
